/**
 * Ajustes por sitio: la misma noticia contada distinto en cada cabecera (§10.2).
 *
 * Es la respuesta al **muro #4**, el del contenido duplicado. Publicar el mismo
 * texto palabra por palabra en cinco dominios es, para un buscador, cinco copias
 * compitiendo entre sí; lo normal es que indexe una y entierre las demás.
 *
 * Hay dos formas de mitigarlo y las dos viven aquí:
 *
 *   1. **Cambiar el texto.** Otro titular y otra entradilla por periódico. Es lo
 *      que un editor haría de todas formas, porque el lector de cada cabecera no
 *      es el mismo.
 *   2. **Decirlo explícitamente.** `canonical` al original o `noindex` en las
 *      copias. Google trata el canonical entre dominios solo como una pista, así
 *      que para copias que de verdad no deben aparecer, `noindex` es lo firme.
 *
 * Cuál usar por defecto lo decide cada organización (§16.5) y se configura por
 * sitio; aquí solo se puede pisar para **esta** noticia.
 *
 * Solo aparece con el artículo ya guardado: las variantes cuelgan de él y no
 * existe dónde ponerlas hasta entonces.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, type ArticleOverride, articlesApi, type Site } from '../lib/api.ts';
import { Button, Field, Input } from './ui.tsx';

export interface SiteOverridesPanelProps {
  articleId: string;
  /** Solo los periódicos elegidos: variantes para sitios que no reciben la nota son ruido. */
  sites: Site[];
}

type Draft = Pick<ArticleOverride, 'title' | 'excerpt' | 'seoTitle' | 'seoDescription' | 'canonicalMode'>;

const EMPTY: Draft = {
  title: null,
  excerpt: null,
  seoTitle: null,
  seoDescription: null,
  canonicalMode: null,
};

const CANONICAL_LABEL: Record<string, string> = {
  '': 'La del sitio',
  none: 'Nada (cada uno compite por su cuenta)',
  origin: 'Canonical al original',
  noindex: 'No indexar esta copia',
};

export function SiteOverridesPanel({ articleId, sites }: SiteOverridesPanelProps) {
  const [open, setOpen] = useState<string | null>(null);

  const { data: article } = useQuery({
    queryKey: ['articles', articleId],
    queryFn: () => articlesApi.get(articleId),
  });

  const saved = new Map((article?.overrides ?? []).map((entry) => [entry.siteId, entry]));

  if (sites.length === 0) {
    return (
      <p className="rounded-lg bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-muted)]">
        Elige periódicos para poder darle a cada uno su propio titular.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-[var(--color-line)] rounded-lg border border-[var(--color-line)]">
      {sites.map((site) => {
        const current = saved.get(site.id);
        const isOpen = open === site.id;

        // Se marca cuáles tienen algo propio: con quince periódicos, abrirlos
        // uno a uno para averiguarlo no es una opción.
        const customized = Boolean(
          current && (current.title || current.excerpt || current.seoTitle || current.canonicalMode),
        );

        return (
          <li key={site.id} className="px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{site.name}</span>

              {customized ? (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                  Con variante
                </span>
              ) : null}

              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : site.id)}
                className="rounded-md border border-[var(--color-line)] px-2 py-1 text-xs hover:bg-[var(--color-surface)]"
              >
                {isOpen ? 'Cerrar' : 'Ajustar'}
              </button>
            </div>

            {isOpen ? <OverrideForm articleId={articleId} site={site} initial={current ?? null} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

function OverrideForm({
  articleId,
  site,
  initial,
}: {
  articleId: string;
  site: Site;
  initial: ArticleOverride | null;
}) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [status, setStatus] = useState<string | null>(null);

  /**
   * Carga lo guardado al abrir.
   *
   * Que esto baste depende del `key` del componente padre: es lo que garantiza
   * que cambiar de periódico monte un formulario nuevo en lugar de reutilizar
   * este con su estado dentro.
   */
  useEffect(() => {
    setDraft(
      initial
        ? {
            title: initial.title,
            excerpt: initial.excerpt,
            seoTitle: initial.seoTitle,
            seoDescription: initial.seoDescription,
            canonicalMode: initial.canonicalMode,
          }
        : EMPTY,
    );
    setStatus(null);
  }, [initial]);

  const save = useMutation({
    mutationFn: () => articlesApi.saveOverride(articleId, site.id, draft),
    onSuccess: () => setStatus('Guardado.'),
    onError: (cause: unknown) => setStatus(cause instanceof ApiError ? cause.message : 'No pudimos guardar.'),
  });

  const set = (patch: Partial<Draft>) => setDraft((previous) => ({ ...previous, ...patch }));

  return (
    <div className="mt-3 rounded-lg bg-[var(--color-surface)] p-3">
      <Field id={`titulo-${site.id}`} label="Titular en este periódico" hint="Vacío usa el titular común.">
        <Input
          id={`titulo-${site.id}`}
          value={draft.title ?? ''}
          onChange={(event) => set({ title: event.target.value || null })}
          maxLength={300}
        />
      </Field>

      <Field id={`extracto-${site.id}`} label="Entradilla en este periódico">
        <Input
          id={`extracto-${site.id}`}
          value={draft.excerpt ?? ''}
          onChange={(event) => set({ excerpt: event.target.value || null })}
          maxLength={1000}
        />
      </Field>

      <Field
        id={`canonical-${site.id}`}
        label="Contenido duplicado"
        hint="Qué decirle a los buscadores sobre esta copia."
      >
        <select
          id={`canonical-${site.id}`}
          value={draft.canonicalMode ?? ''}
          onChange={(event) =>
            set({
              canonicalMode: (event.target.value || null) as Draft['canonicalMode'],
            })
          }
          className="w-full rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm outline-none focus:border-[var(--color-brand)]"
        >
          {Object.entries(CANONICAL_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>

      <Button variant="ghost" loading={save.isPending} onClick={() => save.mutate()}>
        Guardar para {site.name}
      </Button>

      {status ? (
        <p role="status" className="mt-2 text-center text-xs text-[var(--color-muted)]">
          {status}
        </p>
      ) : null}
    </div>
  );
}
